import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { formatINR, fmtDate, fmtDateTime, GST_LABEL } from "@/lib/format";
import { Wordmark } from "@/components/site/Wordmark";
import { Loading, ErrorState, Spinner } from "@/components/site/Primitives";
import { PayPanel, PaymentDone, dueLabel } from "@/components/quote/PayPanel";

const Row = ({ k, v }) => <div className="flex justify-between py-2 text-sm"><span className="text-zinc-500">{k}</span><span className="text-white">{v}</span></div>;

const Respond = ({ token, q, onDone }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const act = async (action) => {
    if (name.trim().length < 2) return setErr("Type your full name to confirm.");
    setBusy(action); setErr(null);
    try { onDone((await api.post(`/public/quotes/${token}/${action}`, { name, confirm: true })).data); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="border border-[#0055FF]/40 bg-[#0055FF]/5 p-6 md:p-8 space-y-4" data-testid="quote-respond-panel">
      <label htmlFor="accept-name" className="block text-sm text-zinc-300">Type your full name to accept this quote and its terms</label>
      <input id="accept-name" className="eb-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="quote-accept-name" />
      <p className="text-sm text-zinc-400" data-testid="quote-due-note">After accepting, you'll pay <span className="text-white">{formatINR(q.amount_due)}</span> ({dueLabel(q).toLowerCase()}) to start the project.</p>
      {err && <p className="text-sm text-[#FF3B30]" role="alert" data-testid="quote-respond-error">{err}</p>}
      <div className="flex flex-wrap gap-3">
        <button className="eb-btn-primary" disabled={!!busy} onClick={() => act("accept")} data-testid="quote-accept-btn">{busy === "accept" && <Spinner />}Accept quote</button>
        <button className="eb-btn-ghost" disabled={!!busy} onClick={() => act("decline")} data-testid="quote-decline-btn">Decline</button>
      </div>
    </div>
  );
};

export default function QuoteView() {
  const { token } = useParams();
  const [q, setQ] = useState(undefined);
  useEffect(() => {
    document.title = "Your quote — EditBay";
    const m = document.createElement("meta"); m.name = "robots"; m.content = "noindex,nofollow"; document.head.appendChild(m);
    api.get(`/public/quotes/${token}`).then((r) => setQ(r.data)).catch(() => setQ(null));
    return () => m.remove();
  }, [token]);
  if (q === undefined) return <div className="eb-container py-24"><Loading rows={1} /></div>;
  if (q === null) return <div className="eb-container py-24"><ErrorState message="This quote link is invalid or no longer available." /></div>;
  const t = q.totals || {};
  return (
    <div className="eb-container py-12 md:py-20 max-w-4xl" data-testid="quote-view-page">
      <div className="flex justify-between items-start border-b border-[#27272A] pb-8">
        <Wordmark size="text-3xl" />
        <div className="text-right"><p className="eb-label">Quote</p><p className="font-display text-xl font-bold" data-testid="quote-number">{q.quote_number}</p><p className="text-xs text-zinc-500">Version {q.version} · {q.enquiry_reference}</p></div>
      </div>
      <p className="mt-10 text-zinc-400">Prepared for <span className="text-white">{q.client_name}</span>{q.company && ` · ${q.company}`}</p>
      <h1 className="mt-3 text-3xl md:text-5xl font-black tracking-tighter">{q.title}</h1>
      <p className="mt-2 eb-label">{q.service_category} edit</p>
      <div className="mt-10 grid md:grid-cols-2 gap-10">
        <div><p className="eb-label mb-3">Deliverables</p>{q.deliverables.map((d) => <p key={d} className="flex gap-2 py-1 text-sm text-zinc-300"><Check size={16} className="text-[#0055FF] mt-0.5 shrink-0" />{d}</p>)}</div>
        <div><p className="eb-label mb-3">Exclusions</p>{q.exclusions.length ? q.exclusions.map((d) => <p key={d} className="flex gap-2 py-1 text-sm text-zinc-500"><X size={16} className="mt-0.5 shrink-0" />{d}</p>) : <p className="text-sm text-zinc-500">None listed</p>}</div>
      </div>
      <div className="mt-10 grid md:grid-cols-2 gap-10">
        <div className="space-y-1">
          <Row k="Delivery schedule" v={q.delivery_schedule} />
          {q.first_draft_hours && <Row k="First draft" v={`Within ${q.first_draft_hours} hours of start`} />}
          <Row k="Revision rounds" v={q.revision_limit} />
          <Row k="Valid until" v={fmtDate(q.valid_until)} />
        </div>
        <div className="bg-[#121212] border border-[#27272A] p-6" data-testid="quote-totals">
          <Row k="Subtotal" v={formatINR(t.subtotal)} />
          <Row k={`GST ${q.gst_mode === "not_applicable" ? "" : `(${q.gst_rate}%)`}`} v={q.gst_mode === "not_applicable" ? "N/A" : formatINR(t.gst_amount)} />
          <div className="border-t border-[#27272A] mt-2 pt-3 flex justify-between"><span className="eb-label">Total</span><span className="font-display text-3xl font-black" data-testid="quote-total">{formatINR(t.total)}</span></div>
          <p className="text-xs text-zinc-500 mt-2">{GST_LABEL[q.gst_mode]}</p>
          {q.deposit_percent < 100 && <div className="mt-3 pt-3 border-t border-[#27272A]"><Row k={`Advance to start (${q.deposit_percent}%)`} v={formatINR(q.amount_due)} /></div>}
        </div>
      </div>
      {q.notes && <p className="mt-10 text-sm text-zinc-400 whitespace-pre-line border-l-2 border-[#27272A] pl-4">{q.notes}</p>}
      <div className="mt-12">
        {q.status === "sent" && <Respond token={token} q={q} onDone={setQ} />}
        {q.status === "accepted" && q.payment_status === "pending" && <PayPanel token={token} q={q} onPaid={setQ} />}
        {q.status === "accepted" && q.payment_status === "paid" && <PaymentDone q={q} />}
        {q.status === "accepted" && !q.payment_status && <p className="text-[#34C759]" data-testid="quote-status-accepted">Accepted by {q.accepted?.name} on {fmtDateTime(q.accepted?.at)}. We'll be in touch with next steps.</p>}
        {["declined", "expired", "superseded"].includes(q.status) && <p className="text-amber-400" data-testid="quote-status-closed">This quote is {q.status}. Contact us if you'd like a refreshed quote.</p>}
      </div>
    </div>
  );
}
